import React, {ChangeEvent, useEffect, useId, useRef, useState} from 'react';
import {
  Select,
  InputLabel,
  FormControl,
  Typography,
  MenuItem,
  Box,
  InputAdornment,
  FormControlProps,
  SelectProps,
  FormHelperText,
  InputLabelProps,
} from '@mui/material';
import './ArrSelectDropdown.scss';
import {ISelectItem} from '../../../shared/types';
import {ReactComponent as SearchIcon} from '../../../../src/assets/images/icon-search.svg';
import {ReactComponent as PinIcon} from '../../../../src/assets/images/pin.svg';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import ArrTextField from '../arr-text-field/ArrTextField';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import ClearIcon from '@mui/icons-material/Clear';
import ArrTooltip from '../arr-tooltip/ArrTooltip';
import {useDropdownKeyboardNavigation} from '../../../hooks/utils/useDropdownKeyboardNavigation';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {t} from 'i18next';
import {POSITION_HEIGHT} from '../../../shared/constants';
import ArrTooltipWrapper from '../arr-tooltip-wrapper/ArrTooltipWrapper';

const {
  AVAILABLE_SPACE,
  RESERVED_HEIGHT_SEARCH_ROW,
  MAX_HEIGHT_MENU,
  DEFAULT_MAX_HEIGHT,
} = POSITION_HEIGHT;

const CLEAR_SELECTION_ID = 'clear_selection';
interface BaseProps {
  dropdownLabel: string;
  dropdownItems: ISelectItem[];
  dropdownItemKey?: string;
  dropdownItemDisplay?: string;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  onBlur?: () => void;
  maxVisibleItems?: number;
  searchable?: boolean;
  sizeVariant?: 'small' | 'medium' | 'large';
  formControlRestProps?: Omit<FormControlProps, 'children'>;
  selectRestProps?: Omit<SelectProps, 'onChange' | 'value' | 'multiple'>;
  customWidth?: string | number;
  dataTestId: string;
  showNoResultsText?: boolean;
  clearSelection?: boolean;
}

export interface ArrSingleSelectProps extends BaseProps {
  autoFocus?: boolean;
  multiple?: false;
  selectedValues: ISelectItem | null;
  onSelectionChange?: (value: ISelectItem | null) => void;
  validateBeforeSettingValue?: (value: ISelectItem | null) => Promise<boolean>;
  toolTip?: boolean;
  keyPressActive?: boolean;
  menuPropClassName?: string;
  renderPrefix?: (item: any) => React.ReactNode; // <--- NEW
  renderSuffix?: (item: any) => React.ReactNode;
  renderSelectedPrefix?: (item: any) => React.ReactNode; // <--- NEW
  infoIcontooltip?: string;
  setAnchorElementWidth?: (width: number) => void;
  className?: string;
  inputLabelClassName?: string;
  inputLabelProps?: InputLabelProps;
  tabIndex?: number;
  ariaLabel?: string;
}

const ArrSingleSelectDropdown: React.FC<ArrSingleSelectProps> = ({
  autoFocus = false,
  dropdownLabel,
  dropdownItems,
  dropdownItemKey = 'id',
  dropdownItemDisplay = 'name',
  selectedValues,
  onSelectionChange,
  disabled = false,
  error = false,
  errorText,
  onBlur,
  searchable = false,
  sizeVariant = 'large',
  formControlRestProps,
  selectRestProps,
  customWidth,
  dataTestId,
  toolTip,
  keyPressActive,
  menuPropClassName,
  renderPrefix,
  renderSuffix,
  renderSelectedPrefix,
  showNoResultsText = false,
  infoIcontooltip = '',
  validateBeforeSettingValue,
  setAnchorElementWidth,
  clearSelection = false,
  className = '',
  inputLabelClassName = '',
  inputLabelProps,
  tabIndex,
  ariaLabel,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ISelectItem | null>(
    selectedValues
  );
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const [searchFieldText, setSearchFieldText] = useState('');

  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');

  const [menuMaxHeight, setMenuMaxHeight] = useState<number>(600);
  const [isTruncatedDisplayText, setIsTruncatedDisplayText] = useState(false);

  const formControlRef = useRef<HTMLDivElement>(null);
  const displayTextRef = useRef<HTMLDivElement>(null);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    if (!customWidth && formControlRef.current) {
      setMenuWidth(formControlRef.current.offsetWidth);
    }
  }, [customWidth, isDropdownOpen]);

  // Dynamic position & height calculation
  useEffect(() => {
    const updateMenuPositionAndHeight = () => {
      if (!formControlRef.current) return;

      const rect = formControlRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Decide dropdown direction
      const position =
        spaceBelow < AVAILABLE_SPACE && spaceAbove > spaceBelow
          ? 'top'
          : 'bottom';
      setMenuPosition(position);

      // Reserve space: search (50) + select all row (50)
      const reservedHeight =
        (searchable ? RESERVED_HEIGHT_SEARCH_ROW : 0) +
        RESERVED_HEIGHT_SEARCH_ROW;

      const availableHeight = position === 'bottom' ? spaceBelow : spaceAbove;
      const maxHeight = Math.min(
        MAX_HEIGHT_MENU,
        availableHeight * 0.7 - reservedHeight
      );
      setMenuMaxHeight(maxHeight > 0 ? maxHeight : DEFAULT_MAX_HEIGHT); // fallback minimal height
    };

    updateMenuPositionAndHeight();
    window.addEventListener('resize', updateMenuPositionAndHeight);
    window.addEventListener('scroll', updateMenuPositionAndHeight, true);

    if (isDropdownOpen && searchable) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    } else if (isDropdownOpen) {
      requestAnimationFrame(() => {
        scrollContainerRef.current?.focus();
      });
    }

    return () => {
      window.removeEventListener('resize', updateMenuPositionAndHeight);
      window.removeEventListener('scroll', updateMenuPositionAndHeight, true);
    };
  }, [isDropdownOpen, searchable]);

 

  const filterItems = <K extends keyof ISelectItem>(
    items: ISelectItem[],
    query: string,
    displayKey: K
  ): ISelectItem[] => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return items;
    return items.filter((item) => {
      const value = item[displayKey];
      return (
        typeof value === 'string' &&
        value.trim().toLowerCase().includes(normalizedQuery)
      );
    });
  };

  const filteredList = filterItems(
    dropdownItems,
    searchFieldText,
    dropdownItemDisplay ?? ''
  );

  const selectAriaLabel =
    (selectRestProps as {['aria-label']?: string})?.['aria-label'] ?? undefined;

  const handleItemClick = async (item: ISelectItem) => {
    if (disabled) return;

    if (
      clearSelection &&
      item?.[dropdownItemKey]?.toString() === CLEAR_SELECTION_ID
    ) {
      setSelectedItem(null);
      onSelectionChange?.(null);
      setIsDropdownOpen(false);
      setSearchFieldText('');
      return;
    }
    // Before updating Dropdown value , Validation can be performed to check if dropdown update is possible or not - Default value TRUE
    const shouldContinue = validateBeforeSettingValue
      ? await validateBeforeSettingValue(item)
      : true;
    if (shouldContinue) {
      setSelectedItem(item);
      onSelectionChange?.(item);
      setIsDropdownOpen(false);
      setSearchFieldText('');
    }
  };

  const clearSelectionItem =
    clearSelection && filteredList.length > 0 && selectedItem !== null
      ? ({
          [dropdownItemKey]: CLEAR_SELECTION_ID,
          [dropdownItemDisplay]: 'Clear Selection',
        } as unknown as ISelectItem)
      : null;

  const itemsToRender = clearSelectionItem
    ? [clearSelectionItem, ...filteredList]
    : filteredList;

  const selectedIndex = itemsToRender?.findIndex(
    (item) =>
      selectedItem?.[dropdownItemKey]?.toString() ===
      item[dropdownItemKey]?.toString()
  );

  const dropdownIdPrefix = dropdownLabel.replace(/\s+/g, '-').toLowerCase();
  const getOptionId = (value?: string) =>
    value ? `arr-dropdown-option-${dropdownIdPrefix}-${value}` : undefined;
  const visualLabelId = `${dropdownIdPrefix}-visual-label`;
  const accessibleNameId = `${dropdownIdPrefix}-accessible-name`;
  const showInputLabel = sizeVariant === 'large' || !selectedItem;
  const accessibleName = selectAriaLabel ?? dropdownLabel;
  const {highlightedIndex, handleKeyDown, setHighlightedIndex} =
    useDropdownKeyboardNavigation<ISelectItem>({
      items: itemsToRender,
      onSelect: handleItemClick,
      isOpen: isDropdownOpen,
      itemDisabled: (item) => !!item.disabled,
      enableKeyboardNavigation: keyPressActive,
      initialIndex: selectedIndex >= 0 ? selectedIndex : -1,
    });

  // The keyboard highlight is a position in the rendered list, so searching
  // re-indexes it onto whichever option now sits at that position. Re-anchor it
  // on the selected option, and drop it when the search filters that one out.
  useEffect(() => {
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : -1);
  }, [searchFieldText]);

  useEffect(() => setSelectedItem(selectedValues), [selectedValues]);

  useEffect(() => {
    const el = displayTextRef.current;
    if (el) setIsTruncatedDisplayText(el.scrollWidth > el.clientWidth);
  }, [selectedValues, dropdownItemDisplay]);

  const searchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchFieldText(event.target.value);
  };

  const handleCloseAndClear = () => {
    setIsDropdownOpen(false);
    setSearchFieldText('');
    onBlur?.();
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    const item = itemRefs.current[highlightedIndex];
    if (!container || !item || highlightedIndex < 0) return;
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const containerTop = container.scrollTop;
    const containerBottom = containerTop + container.clientHeight;
    if (itemBottom > containerBottom) {
      container.scrollTop = itemBottom - container.clientHeight;
    }
    if (itemTop < containerTop) {
      container.scrollTop = itemTop;
    }
  }, [highlightedIndex]);

  const renderSelected = (selectedValue: ISelectItem | null) => {
    const selectedLabel = selectedValue?.[dropdownItemDisplay] ?? '';
    return (
      <ArrTooltip
        content={selectedLabel}
        showOnTruncate
        isTruncated={isTruncatedDisplayText}
        setAnchorElementWidth={setAnchorElementWidth}
        className="arr-dropdown-selected-container"
        focusableWrapper
        labelId={`selected-item-tooltip-${dataTestId}`}
      >
        {selectedValues?.isPin && (
          <PinIcon className="arr-dropdown-option-pin-icon" />
        )}
        <Typography
          ref={displayTextRef}
          noWrap
          className={`arr-dropdown-selected${
            disabled ? ' arr-dropdown-selected--disabled' : ''
          }`}
          data-testid={`arr-single-select-selected-${dataTestId}`}
        >
          {selectedLabel}
        </Typography>
        {selectedLabel && (
          <span className="sr-only">{` ${t('common.selected')}`}</span>
        )}
      </ArrTooltip>
    );
  };

  const normalizeWidth = (val?: string | number) =>
    typeof val === 'number' ? `${val}rem` : val;

  const dynamicWidth = customWidth ? normalizeWidth(customWidth) : undefined;

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const activeOptionId =
    highlightedIndex >= 0 && itemsToRender?.[highlightedIndex]
      ? getOptionId(
          itemsToRender[highlightedIndex]?.[dropdownItemKey]?.toString()
        )
      : undefined;

  const handleKeyDownNormalized = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      handleKeyDown({
        ...e,
        key: 'ArrowDown',
      } as unknown as React.KeyboardEvent);
      return;
    }
    if (e.key === 'ArrowDown') {
      handleKeyDown({...e, key: 'ArrowUp'} as unknown as React.KeyboardEvent);
      return;
    }
    handleKeyDown(e);
  };

  return (
    <FormControl
      variant="filled"
      ref={formControlRef}
      className={`arr-dropdown-container arr-dropdown-container--${sizeVariant} ${className}`}
      disabled={disabled}
      fullWidth={!dynamicWidth}
      style={
        dynamicWidth
          ? {
              width: dynamicWidth,
              minWidth: dynamicWidth,
              maxWidth: dynamicWidth,
              flex: '0 0 auto',
            }
          : {flex: '1 1 auto'}
      }
      error={error || !!errorText}
      data-testid={`wrapper-${dataTestId}`}
      onKeyDown={disabled ? undefined : handleKeyDown}
      {...formControlRestProps}
    >
      {showInputLabel && (
        <InputLabel
          id={visualLabelId}
          className={`arr-dropdown-label ${inputLabelClassName}`}
          tabIndex={-1}
          {...inputLabelProps}
        >
          {dropdownLabel}
        </InputLabel>
      )}
      <span id={accessibleNameId} className="sr-only">
        {accessibleName}
      </span>
      <Select
        tabIndex={tabIndex}
        onBlur={onBlur}
        open={isDropdownOpen}
        onOpen={() => !disabled && setIsDropdownOpen(true)}
        onClose={handleCloseAndClear}
        labelId={accessibleNameId}
        value={selectedItem ?? ''}
        renderValue={(selected) => renderSelected(selected as ISelectItem)}
        displayEmpty
        inputProps={{'data-testid': `${dataTestId}-input-field`}}
        onKeyDown={(e) => {
          if (disabled) {
            return;
          }
          //Prevent Opening Select from Tab Key, we require default behavior for Tab key for accessibility
          if (e.key === 'Tab' || e.key === 'Shift') {
            return;
          }
          if (!isDropdownOpen) {
            setIsDropdownOpen(true);
          }
          handleKeyDownNormalized(e);
        }}
        IconComponent={() => (
          <>
            {renderSelectedPrefix && selectedItem && (
              <span
                className="dropdown-prefix"
                data-testid={`arr-single-select-prefix-${dataTestId}`}
              >
                {renderSelectedPrefix(selectedItem)}
              </span>
            )}
            {infoIcontooltip && (
              <ArrTooltipWrapper
                title={t('tagsAndFolders.modal.tagClassificationTooltipTitle')}
                trigger={
                  <InfoIcon
                    fontSize="medium"
                    className="arr-dropdown-option-info-icon"
                  />
                }
              >
                {infoIcontooltip}
              </ArrTooltipWrapper>
            )}
            <ArrowDropDownIcon
              onClick={() => !disabled && setIsDropdownOpen((prev) => !prev)}
              style={{
                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                cursor: disabled ? '' : 'pointer',
                width: 'var(--font-size-subhead2)',
                height: 'var(--font-size-subhead2)',
                marginRight: 'var(--dropdown-icon-margin)',
              }}
              sx={{
                '& path': {
                  transform: 'scale(1.2, 1.2)',
                  transformOrigin: 'center',
                },
              }}
              data-testid={`dropdown-arrow-drop-down-icon-${dataTestId}`}
            />
          </>
        )}
        disabled={disabled}
        autoWidth={false}
        className={`arr-dropdown-container__arr-dropdown-select arr-dropdown-container__arr-dropdown-select--${sizeVariant}`}
        MenuProps={{
          PaperProps: {
            className: `${menuPropClassName} arr-dropdown-container__arr-dropdown-menu arr-dropdown-container__arr-dropdown-menu--${menuPosition} arr-dropdown-container__arr-dropdown-menu--${sizeVariant} `,
            style: dynamicWidth ? {width: dynamicWidth} : {width: menuWidth},
          },
        }}
        data-testid={`arr-select-dropdown-${dataTestId}`}
        SelectDisplayProps={{'data-testid': `${dataTestId}`}}
        {...selectRestProps}
      >
        {searchable && (
          <Box
            className="arr-dropdown-search"
            data-testid="arr-dropdown-search"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <ArrTextField
              inputRef={searchInputRef}
              fullWidth
              id="dropdown-search"
              placeholder="Search"
              sizeVariant="medium"
              onChange={searchChangeHandler}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  return;
                }
                handleKeyDownNormalized(e);
                e.stopPropagation();
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              value={searchFieldText}
              dataTestId="single-select-dropdown-search-input"
              slotProps={{
                inputLabel: {shrink: false},
                input: {
                  autoFocus: true,
                  role:"searchbox",
                  'aria-label': `${dropdownLabel} search`,
                  'aria-controls': `${dropdownLabel}-listbox`,
                  'aria-autocomplete': 'list',
                  endAdornment: searchFieldText.length ? (
                    <InputAdornment position="end">
                      <ArrIconButton
                        icon={<ClearIcon />}
                        onClick={() => setSearchFieldText('')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.code === 'Space') {
                            setSearchFieldText('');
                          }
                        }}
                        ariaLabel="clear"
                        shape="none"
                        size="small"
                        testId={`dropdown-clear-icon-button-${dropdownLabel}`}
                      />
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      <ArrIconButton
                        icon={<SearchIcon />}
                        ariaLabel="search"
                        shape="none"
                        size="small"
                        testId={`dropdown-search-icon-button-${dropdownLabel}`}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        )}
        <Box
          className="arr-dropdown-scroll"
          ref={scrollContainerRef}
          role="listbox"
          data-testid={`arr-single-dropdown-menu-wrapper`}
          aria-activedescendant={activeOptionId}
          tabIndex={0}
          aria-label={selectAriaLabel ?? dropdownLabel}
          onKeyDown={handleKeyDownNormalized}
          style={{maxHeight: menuMaxHeight}}
        >
          {itemsToRender?.map((item, index) => {
            const key = item[dropdownItemKey]?.toString();
            const label = item[dropdownItemDisplay]?.toString();
            const isSelected =
              selectedItem?.[dropdownItemKey]?.toString() === key;
            const isHighlighted = index === highlightedIndex;
            const isClearSelectionRow =
              clearSelection && key === CLEAR_SELECTION_ID;
            return (
              <MenuItem
                key={key}
                id={getOptionId(key)}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                tabIndex={-1}
                value={key}
                onClick={() => handleItemClick(item)}
                selected={isSelected || isHighlighted}
                disabled={!!item.disabled}
                role="option"
                aria-selected={isSelected}
                className={`arr-dropdown-option-item 
 ${isSelected ? 'arr-dropdown-option-item--selected' : ''} 
 ${isHighlighted ? 'arr-dropdown-option-item--highlighted' : ''}
 ${isClearSelectionRow ? 'arr-dropdown-option-item--clear-selection' : ''}`}
                data-testid={`arr-single-select-dropdown-menu-item-${item.id ?? index}`}
              >
                {renderPrefix && (
                  <span
                    className="dropdown-prefix"
                    data-testid={`arr-single-select-prefix-${dataTestId}-${index}`}
                  >
                    {renderPrefix(item)}
                  </span>
                )}
                {toolTip ? (
                  <ArrTooltip content={label} showOnTruncate>
                    <Typography
                      className="arr-dropdown-option"
                      data-testid={`arr-single-select-prefix-label-tooltip-${dataTestId}-${index}`}
                    >
                      {label}
                    </Typography>
                  </ArrTooltip>
                ) : (
                  <Box className="arr-dropdown-option-container">
                    {item?.isPin && (
                      <PinIcon className="arr-dropdown-option-pin-icon" />
                    )}
                    <Typography
                      className="arr-dropdown-option"
                      data-testid={`arr-single-select-label--${dataTestId}-${index}`}
                    >
                      {label}
                    </Typography>
                  </Box>
                )}
                {renderSuffix && (
                  <span
                    className="dropdown-suffix"
                    data-testid={`arr-single-select-suffix-${dataTestId}-${index}`}
                  >
                    {renderSuffix(item)}
                  </span>
                )}
              </MenuItem>
            );
          })}
          {showNoResultsText && filteredList.length == 0 && (
            <Box className="arr-dropdown-option-item arr-dropdown--no-results">
              <Typography
                className="arr-dropdown-option"
                data-testid={`arr-single-select-dropdown-no-result-found-${dataTestId}`}
              >
                {t('common.noResultFound')}
              </Typography>
            </Box>
          )}
        </Box>
      </Select>
      {errorText && (
        <FormHelperText data-testid="arr-dropdown-error">
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default ArrSingleSelectDropdown;
